<template>
  <q-input
    :value="formattedValue"
    :label="label"
    :placeholder="placeholder"
    :disable="disable"
    :readonly="readonly"
    :dense="dense"
    :filled="filled"
    :outlined="outlined"
    :borderless="borderless"
    :standout="standout"
    :hide-bottom-space="hideBottomSpace"
    :input-class="inputClass"
    :error="error"
    :error-message="errorMessage"
    :rules="sanitizedRules"
    :lazyRules="lazyRules"
    :stack-label="stackLabel"
    :clearable="clearable"
    :prefix="prefix"
    :suffix="suffix"
    :debounce="debounce"
    :maxlength="maxlength"
    :autogrow="autogrow"
    :counter="counter"
    :behavior="behavior"
    @input="updateValue"
    @keypress="handleKeyPress"
    @blur="$emit('blur', rawValue)"
  >
    <template
      v-for="(slotFn, slotName) in $scopedSlots"
      v-slot:[slotName]="slotProps"
    >
      <slot :name="slotName" v-bind="slotProps" />
    </template>
  </q-input>
</template>
<script>

export default {
  name: 'QInputNumber',
  props: {
    value: [String, Number],
    label: String,
    placeholder: String,
    disable: Boolean,
    readonly: Boolean,
    dense: Boolean,
    filled: Boolean,
    outlined: Boolean,
    borderless: Boolean,
    standout: Boolean,
    hideBottomSpace: Boolean,
    type: {
      type: String,
      default: 'text'
    },
    inputClass: String,
    decimalPlaces: {
      type: Number,
      default: 8
    },
    error: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: ''
    },
    rules: {
      type: Array,
      default: () => []
    },
    lazyRules: {
      type: Boolean,
      default: false
    },
    stackLabel: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    },
    prefix: {
      type: String,
      default: ''
    },
    suffix: {
      type: String,
      default: ''
    },
    debounce: {
      type: [String, Number],
      default: 0
    },
    maxlength: {
      type: [String, Number],
      default: null
    },
    autogrow: {
      type: Boolean,
      default: false
    },
    counter: {
      type: Boolean,
      default: false
    },
    behavior: {
      type: String,
      default: 'default'
    }
  },
  computed: {
    formattedValue () {
      return (this.value !== null && this.value !== undefined) ? this.formatNumber(this.value) : ''
    },
    rawValue () {
      return (this.value !== null && this.value !== undefined) ? this.value.toString().replace(/,/g, '') : ''
    },
    sanitizedRules () {
      return this.rules.map(rule => val => {
        const sanitizedVal = typeof val === 'string' ? val.replace(/,/g, '') : val
        return rule(sanitizedVal)
      })
    }
  },
  methods: {
    formatNumber (value) {
      if (value === null || value === undefined) return ''
      const num = value.toString().replace(/,/g, '')
      if (isNaN(num)) return ''

      const parts = num.split('.')
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      return parts.join('.')
    },
    updateValue (event) {
      let numericValue = event.replace(/,/g, '')
      if (this.type === 'number') {
        numericValue = numericValue.replace(/[^0-9.]/g, '')
        const decimalCount = (numericValue.match(/\./g) || []).length
        if (decimalCount > 1) {
          numericValue = numericValue.replace(/\.+$/, '')
        }
        const parts = numericValue.split('.')
        if (parts.length === 2 && parts[1].length > this.decimalPlaces) {
          parts[1] = parts[1].slice(0, this.decimalPlaces)
          numericValue = parts.join('.')
        }
      }
      if (numericValue === '.') {
        numericValue = '0.'
      }

      if (numericValue !== '' && (!isNaN(numericValue) || numericValue === '0.')) {
        this.$emit('input', numericValue)
      }
    },
    handleKeyPress (event) {
      if (this.type === 'number') {
        if (!/[0-9.]/.test(event.key)) {
          event.preventDefault()
        }

        if (event.key === '.' && this.rawValue.includes('.')) {
          event.preventDefault()
        }
      }
    }
  }
}
</script>
